/**
 * Season standings for the host (route /seasons/:id). Shows the rule, a
 * warning when some teams in the season have no persistent team, the
 * standings table, and each team's nights on click.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertDialog,
  Badge,
  Button,
  Callout,
  Flex,
  Heading,
  Link as RadixLink,
  Text,
} from '@radix-ui/themes';
import { fetchVenueOptions, type VenueOption } from '../Teams/types';
import { describeRule, fmtSeasonDate } from '../../utils/seasons';
import SeasonForm from './SeasonForm';
import StandingsTable from './StandingsTable';
import {
  deleteSeason,
  errorMessage,
  fetchSeasonStandings,
  type SeasonStandingsResponse,
  type StandingRow,
} from './seasonsApi';
import '../Teams/Teams.css';

const SeasonDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const seasonId = Number(id);
  const navigate = useNavigate();
  const [data, setData] = useState<SeasonStandingsResponse | null>(null);
  const [venues, setVenues] = useState<VenueOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchSeasonStandings(seasonId));
    } catch (err) {
      setError(errorMessage(err, 'Failed to load the season.'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [seasonId]);

  useEffect(() => {
    load();
    fetchVenueOptions().then(setVenues).catch(() => {});
  }, [load]);

  const handleDelete = async () => {
    try {
      await deleteSeason(seasonId);
      navigate(`/seasons?venueId=${data?.season.venueId ?? ''}`);
    } catch (err) {
      setError(errorMessage(err, 'Failed to delete the season.'));
    }
  };

  if (loading && !data) return <div className="teams-page"><div className="teams-empty">Loading season…</div></div>;
  if (!data) {
    return (
      <div className="teams-page">
        <div className="teams-error">{error ?? 'Season not found.'}</div>
        <Button variant="soft" onClick={() => navigate('/seasons')}>Back to seasons</Button>
      </div>
    );
  }

  const { season, rows, nights, totalNights } = data;
  const unlinkedCount = data.unlinkedCount ?? 0;
  const associateUrl = `/teams/associate?venueId=${season.venueId}&seasonId=${season.id}`;
  const publicPath = `/standings/${season.id}`;

  const rowActions = (r: StandingRow) => (
    <Flex gap="3" mt="2" wrap="wrap">
      {r.canonicalTeamId ? (
        <RadixLink asChild size="2"><Link to={`/teams/${r.canonicalTeamId}`}>Open team history</Link></RadixLink>
      ) : (
        <RadixLink asChild size="2"><Link to={associateUrl}>Link this team to a persistent team</Link></RadixLink>
      )}
    </Flex>
  );

  return (
    <div className="teams-page">
      <Text size="2">
        <RadixLink asChild><Link to={`/seasons?venueId=${season.venueId}`}>← Seasons</Link></RadixLink>
      </Text>
      <div className="teams-header" style={{ marginTop: '0.5rem' }}>
        <div>
          <Heading size="6">
            {season.name}
            {!season.isPublic && <Badge ml="2" color="gray" variant="soft">Private</Badge>}
          </Heading>
          <Text as="div" size="2" color="gray">
            {season.venue?.name} · {fmtSeasonDate(season.startDate)} – {fmtSeasonDate(season.endDate)} ·{' '}
            {totalNights} game night{totalNights === 1 ? '' : 's'} played
          </Text>
          <Text as="div" size="2" color="gray">{describeRule(season)}</Text>
        </div>
        <Flex gap="2" wrap="wrap">
          {season.isPublic && (
            <Button variant="soft" asChild>
              <a href={publicPath} target="_blank" rel="noreferrer">Public page</a>
            </Button>
          )}
          <Button variant="soft" onClick={() => setEditOpen(true)}>Edit</Button>
          <AlertDialog.Root>
            <AlertDialog.Trigger>
              <Button variant="soft" color="red">Delete</Button>
            </AlertDialog.Trigger>
            <AlertDialog.Content maxWidth="420px">
              <AlertDialog.Title>Delete this season?</AlertDialog.Title>
              <AlertDialog.Description size="2">
                This removes the season and its public page. Game nights, teams and scores do not change.
              </AlertDialog.Description>
              <Flex gap="3" mt="4" justify="end">
                <AlertDialog.Cancel>
                  <Button variant="soft" color="gray">Cancel</Button>
                </AlertDialog.Cancel>
                <AlertDialog.Action>
                  <Button color="red" onClick={handleDelete}>Delete</Button>
                </AlertDialog.Action>
              </Flex>
            </AlertDialog.Content>
          </AlertDialog.Root>
        </Flex>
      </div>

      {error && <div className="teams-error">{error}</div>}

      {unlinkedCount > 0 && (
        <Callout.Root mb="3" color="amber" role="status">
          <Callout.Text>
            {unlinkedCount} team entr{unlinkedCount === 1 ? 'y' : 'ies'} in this season{' '}
            {unlinkedCount === 1 ? 'is' : 'are'} not linked to a persistent team. Each one shows as a
            separate row marked “unlinked”, so a team&apos;s points can be split across rows.{' '}
            <RadixLink asChild><Link to={associateUrl}>Link them now</Link></RadixLink>.
          </Callout.Text>
        </Callout.Root>
      )}

      {rows.length === 0 ? (
        <div className="teams-empty">
          <Text as="p" size="3" weight="medium">No scored game nights in this season yet.</Text>
          <Text as="p" size="2" color="gray">
            Standings show here after the first game night in the date range has scores.
          </Text>
        </div>
      ) : (
        <StandingsTable
          rows={rows}
          nights={nights}
          showAttendance={season.attendanceBonus > 0}
          operator
          renderRowActions={rowActions}
        />
      )}

      <SeasonForm
        open={editOpen}
        onOpenChange={setEditOpen}
        venues={venues}
        defaultVenueId={season.venueId}
        season={season}
        onSaved={() => load()}
      />
    </div>
  );
};

export default SeasonDetail;
